import {pickBy, pick, omit, isArray, isPlainObject, omitBy} from 'lodash';

export interface ApiFilterParams {
  [key: string]: any;
}

export interface ApiParams {
  page?: string;
  size?: string;
  sort?: string[];
  filterMap?: ApiFilterParams;
}

export const DEFAULT_API_PARAMS_KEYS = ['page', 'size', 'sort', 'filterMap'];

export function normalizeApiParams(params, allowedKeys?: string[]): ApiParams {
  // get default keys
  const normalizedParams = pick(params, DEFAULT_API_PARAMS_KEYS.concat(allowedKeys ?? []));

  if (params.filterMap) {
    // removed filters with undefined value
    normalizedParams.filterMap = pickBy(params.filterMap, (value) => value !== undefined);
  }

  if (params.page !== undefined) {
    // page in api is counted from 0
    normalizedParams.page = String(params.page - 1);
  }

  if (params.sort) {
    // transform sorting params format
    if (isArray(params.sort)) {
      normalizedParams.sort = params.sort.map((value) => {
        return `${value.colId},${value.sort}`;
      });
    } else {
      normalizedParams.sort = [params.sort];
    }
  }

  if (params.size) {
    normalizedParams.size = String(params.size);
  }
  return normalizedParams;
}

export const transformUrlSearchParams = (apiParameters: ApiParams): URLSearchParams => {
  const paramsWithoutSort = new URLSearchParams(omit(apiParameters, 'sort'));
  let newSortApiParameters = '';

  if (apiParameters?.sort) {
    newSortApiParameters = `sort=${apiParameters.sort.join('&sort=')}`;
  }

  return new URLSearchParams(`${newSortApiParameters}${paramsWithoutSort.toString() === '' ? '' : '&'}${paramsWithoutSort.toString()}`);
};

export function serializeJSONToString(jsonData: Record<string, any>): string {
  // TODO: JSON.stringify is not deterministic, we should replace this with something else
  return JSON.stringify(jsonData);
}

export function isNullsy(ref: any): ref is null | undefined {
  return ref === undefined || ref === null;
}
export function omitNullsy(obj: any, omitEmptyString?: boolean): any {
  return omitBy(obj, (value, _) => value === undefined || value === null || (omitEmptyString && value === ''));
}
/**
 * The object without the values that filter nothing, at any depth: null, undefined, a blank string, an empty array, and
 * a nested object left with no keys. A non-empty array is kept as it is.
 */
export function omitEmptyDeep(obj: Record<string, any>): Record<string, any> {
  const result: Record<string, any> = {};
  Object.entries(obj ?? {}).forEach(([key, value]) => {
    const cleaned = isPlainObject(value) ? omitEmptyDeep(value) : value;
    if (isNullsy(cleaned)) return;
    if (typeof cleaned === 'string' && cleaned.trim() === '') return;
    if (isArray(cleaned) && cleaned.length === 0) return;
    if (isPlainObject(cleaned) && Object.keys(cleaned).length === 0) return;
    result[key] = cleaned;
  });
  return result;
}
export function omitUndefined(obj: any): any {
  return omitBy(obj, (value, _) => value === undefined);
}
export function omitPredicate(obj: any, predicate: (propValue: any) => boolean) {
  return omitBy(obj, (value, _) => predicate(value));
}
